import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Logo } from '@/components/Logo';
import { Users, ShieldCheck, Loader2, ArrowRight, AlertTriangle } from 'lucide-react';

export default function FamilyJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const [state, setState] = useState('loading'); // loading | ready | notfound | joined
  const [ownerName, setOwnerName] = useState('');
  const [full, setFull] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setState('loading');
    api.familyInviteInfo(token)
      .then((d) => { setOwnerName(d.owner_name || ''); setFull(!!d.full); setState('ready'); })
      .catch(() => setState('notfound'));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const join = async () => {
    setBusy(true);
    try {
      const res = await api.familyJoin(token, { member_name: name.trim() });
      setOwnerName(res.owner_name || ownerName);
      setState('joined');
      api.track('family_joined');
    } catch (e) {
      if (e?.response?.status === 409) { setFull(true); toast.error(t('join_full')); }
      else if (e?.response?.status === 404) { setState('notfound'); }
      else toast.error(t('fam_err'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--background)' }}>
      <div className="mx-auto w-full max-w-md px-5 pt-6 pb-10 flex-1 flex flex-col">
        <div className="mb-8"><Logo size={30} /></div>

        {state === 'loading' && (
          <div className="flex-1 flex items-center justify-center" data-testid="join-loading">
            <Loader2 className="animate-spin" style={{ color: 'var(--primary)' }} />
            <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>{t('join_loading')}</span>
          </div>
        )}

        {state === 'notfound' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="join-notfound">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: 'color-mix(in srgb, var(--warning) 16%, transparent)' }}>
              <AlertTriangle size={28} style={{ color: 'var(--warning)' }} />
            </div>
            <p className="font-semibold" style={{ color: 'var(--text)' }}>{t('join_notfound')}</p>
            <button onClick={() => navigate('/app')} className="mt-6 inline-flex items-center gap-2 rounded-full font-bold min-h-[48px] px-6" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="join-go-home">
              {t('join_go')} <ArrowRight size={18} />
            </button>
          </div>
        )}

        {state === 'ready' && (
          <div className="flex-1 flex flex-col justify-center" data-testid="join-ready">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'color-mix(in srgb, var(--primary) 14%, transparent)' }}>
              <Users size={32} style={{ color: 'var(--primary)' }} />
            </div>
            <h1 className="text-2xl font-extrabold" style={{ color: 'var(--text)' }}>{t('join_title')}</h1>
            <p className="text-base font-semibold mt-1" style={{ color: 'var(--primary)' }} data-testid="join-owner">
              {t('join_by').replace('{owner}', ownerName || 'A KilalaRide family')}
            </p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>{t('join_b')}</p>

            {full ? (
              <div className="mt-6 rounded-xl px-4 py-3 flex items-center gap-2 text-sm font-semibold" style={{ background: 'color-mix(in srgb, var(--warning) 16%, transparent)', color: 'var(--warning)' }} data-testid="join-full">
                <AlertTriangle size={16} /> {t('join_full')}
              </div>
            ) : (
              <div className="mt-6">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('join_name_ph')}
                  data-testid="join-name-input"
                  className="w-full rounded-2xl px-4 min-h-[52px] border text-base outline-none"
                  style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text)' }}
                />
                <button
                  onClick={join}
                  disabled={busy}
                  data-testid="join-submit-button"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[52px] disabled:opacity-60"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />} {t('join_cta')}
                </button>
              </div>
            )}
          </div>
        )}

        {state === 'joined' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="join-success">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'color-mix(in srgb, var(--success) 16%, transparent)' }}>
              <ShieldCheck size={34} style={{ color: 'var(--success)' }} />
            </div>
            <h1 className="text-2xl font-extrabold" style={{ color: 'var(--text)' }}>{t('join_success_t')}</h1>
            <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
              {t('join_success_b').replace('{owner}', ownerName || 'your family')}
            </p>
            <button onClick={() => navigate('/app')} className="mt-6 inline-flex items-center gap-2 rounded-full font-bold min-h-[48px] px-6" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="join-open-app">
              {t('join_go')} <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
